import { Page, PageTitle } from "@/components/page"
import { CRUDLoader } from "@/components/page/crudloader"
import { CRUDToolbar } from "@/components/page/crudtoolbar"
import { useDatabaseItem, usePage } from "@/components/page/hooks"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { zodResolver } from "@hookform/resolvers/zod"
import { FileText, Plus, Trash2, X, Zap, Sigma, GitBranch, ArrowLeft } from "lucide-react"
import { useFieldArray, useForm } from "react-hook-form"
import { z } from "zod"
import { CONDITION_OPERATORS, CONDITION_OPERATOR_LABELS } from "@/lib/agents/formlogic"

export const forms_crud = {
  title: "Form",
  route: "/evaluations/forms/:id",
  component,
  group: "evaluations",
  resource: "forms",
  icon: FileText,
  action: "read",
} satisfies PageConfig

const ListItemSchema = z.object({
  label: z.string(),
  value: z.string(),
})

const ExtractionSchema = z.object({
  enabled: z.boolean().optional(),
  method: z.enum(["ai", "keyword"]).optional(),
  instruction: z.string().optional(),
  minConfidence: z.number().min(0).max(1).optional(),
  lockAfterHuman: z.boolean().optional(),
  keywords: z.array(z.string()).optional(),
  keywordMatch: z.enum(["any", "all", "phrase"]).optional(),
  keywordValue: z.string().optional(),
})

const ConditionSchema = z.object({
  field: z.string(),
  operator: z.enum(["equals", "notEquals", "contains", "notContains", "gt", "lt", "gte", "lte", "isEmpty", "isNotEmpty"]),
  value: z.union([z.string(), z.number(), z.boolean()]).optional(),
})

const SubFieldSchema = z.object({
  name: z.string().min(1, "Name is required"),
  title: z.string().min(1, "Title is required"),
  required: z.boolean().optional(),
  type: z.enum(["string", "number", "boolean", "date", "list", "multiselect"]),
  list: z.array(ListItemSchema).optional(),
})

const FieldSchema = z.object({
  name: z.string().min(1, "Name is required"),
  title: z.string().min(1, "Title is required"),
  required: z.boolean().optional(),
  type: z.enum(["string", "number", "boolean", "date", "list", "multiselect", "group"]),
  list: z.array(ListItemSchema).optional(),
  subfields: z.array(SubFieldSchema).optional(),
  extraction: ExtractionSchema.optional(),
  visibleWhen: z.array(ConditionSchema).optional(),
  calculated: z.object({ formula: z.string().optional() }).optional(),
})

const FormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  fields: z.array(FieldSchema).optional(),
})

type FormData = z.infer<typeof FormSchema>

export function component() {

  const { team } = usePage()
  const { data, loading, submit, toastSuccess, navigate } = useDatabaseItem("forms")

  const form = useForm<FormData>({
    resolver: zodResolver(FormSchema),
    values: data as FormData,
  })

  const { fields, append, remove } = useFieldArray({ control: form.control, name: "fields" })

  async function onSubmit(data: FormData) {
    const payload = { ...data, team: team?.id }
    console.log("[form save] fields:", JSON.stringify(data.fields, null, 2))
    await submit(payload as any)
    toastSuccess()
    navigate("/evaluations/forms")
  }

  if (loading) return <CRUDLoader />

  return <Page>
    <div className="pb-8">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Button type="button" variant="ghost" size="sm" className="h-7 text-xs gap-1.5 mb-2"
            onClick={() => navigate("/evaluations/forms")}>
            <ArrowLeft className="h-3 w-3" />
            Back to forms
          </Button>
          <PageTitle>
            <CRUDToolbar table="forms" onSuccess={"/evaluations/forms"} />
          </PageTitle>

          <div className="rgrid">
            <div className="row">
              <div className="rc-12">
                <FormField control={form.control} name="title" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            </div>

            <div className="row">
              <div className="rc-12 flex items-center justify-between">
                <Label className="text-base font-semibold">Fields</Label>
                <Button type="button" variant="outline" className="rounded-lg"
                  onClick={() => append({ name: "", title: "", type: "string", required: false, list: [] })}>
                  <Plus className="h-4 w-4" />Add Field
                </Button>
              </div>
            </div>

            {fields.map((field, index) => (
              <FieldRow key={field.id} form={form} index={index} remove={remove} />
            ))}
          </div>
        </form>
      </Form>
    </div>
  </Page>

}

function FieldRow({ form, index, remove }: { form: ReturnType<typeof useForm<FormData>>, index: number, remove: (i: number) => void }) {

  const type = form.watch(`fields.${index}.type`)
  const extractionEnabled = form.watch(`fields.${index}.extraction.enabled`)
  const method = form.watch(`fields.${index}.extraction.method`) || "ai"
  const calcFormula = form.watch(`fields.${index}.calculated.formula`)
  const isCalculated = !!calcFormula
  const { fields: listItems, append: appendItem, remove: removeItem } = useFieldArray({ control: form.control, name: `fields.${index}.list` })
  const { fields: subfields, append: appendSub, remove: removeSub } = useFieldArray({ control: form.control, name: `fields.${index}.subfields` })
  const { fields: conditions, append: appendCond, remove: removeCond } = useFieldArray({ control: form.control, name: `fields.${index}.visibleWhen` })
  const keywords = form.watch(`fields.${index}.extraction.keywords`) || []

  return <div className="row border rounded-lg p-4 mx-1 mb-2 relative">
    <Button type="button" variant="ghost" size="icon" className="absolute top-2 right-2" onClick={() => remove(index)}>
      <Trash2 className="h-4 w-4 text-destructive" />
    </Button>
    <div className="rc-3">
      <FormField control={form.control} name={`fields.${index}.name`} render={({ field }) => (
        <FormItem>
          <FormLabel>Name</FormLabel>
          <FormControl><Input {...field} /></FormControl>
          <FormMessage />
        </FormItem>
      )} />
    </div>
    <div className="rc-3">
      <FormField control={form.control} name={`fields.${index}.title`} render={({ field }) => (
        <FormItem>
          <FormLabel>Title</FormLabel>
          <FormControl><Input {...field} /></FormControl>
          <FormMessage />
        </FormItem>
      )} />
    </div>
    <div className="rc-2">
      <FormField control={form.control} name={`fields.${index}.type`} render={({ field }) => (
        <FormItem>
          <FormLabel>Type</FormLabel>
          <Select value={field.value} onValueChange={field.onChange} defaultValue={field.value}>
            <FormControl>
              <SelectTrigger><SelectValue placeholder="Select a type" /></SelectTrigger>
            </FormControl>
            <SelectContent>
              <SelectItem value="string">Text</SelectItem>
              <SelectItem value="number">Number</SelectItem>
              <SelectItem value="boolean">Boolean</SelectItem>
              <SelectItem value="date">Date</SelectItem>
              <SelectItem value="list">List (single)</SelectItem>
              <SelectItem value="multiselect">Multi-select</SelectItem>
              <SelectItem value="group">Repeating group</SelectItem>
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )} />
    </div>
    <div className="rc-4">
      <FormField control={form.control} name={`fields.${index}.required`} render={({ field }) => (
        <FormItem className="flex items-center gap-2 pt-8">
          <FormControl>
            <Checkbox checked={field.value} onCheckedChange={field.onChange} />
          </FormControl>
          <FormLabel className="mt-0!">Required</FormLabel>
        </FormItem>
      )} />
    </div>

    {(type === "list" || type === "multiselect") && (
      <div className="rc-12 mt-2">
        <div className="flex items-center justify-between mb-2">
          <Label>{type === "multiselect" ? "Options" : "List Items"}</Label>
          <Button type="button" variant="outline" size="sm" className="rounded-lg" onClick={() => appendItem({ label: "", value: "" })}>
            <Plus className="h-4 w-4" />Add Item
          </Button>
        </div>
        {listItems.map((item, itemIndex) => (
          <div key={item.id} className="flex items-center gap-2 mb-2">
            <FormField control={form.control} name={`fields.${index}.list.${itemIndex}.label`} render={({ field }) => (
              <FormItem className="grow">
                <FormControl><Input placeholder="Label" {...field} /></FormControl>
              </FormItem>
            )} />
            <FormField control={form.control} name={`fields.${index}.list.${itemIndex}.value`} render={({ field }) => (
              <FormItem className="grow">
                <FormControl><Input placeholder="Value" {...field} /></FormControl>
              </FormItem>
            )} />
            <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(itemIndex)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
    )}

    {/* Repeating group */}
    {type === "group" && (
      <div className="rc-12 mt-2">
        <div className="flex items-center justify-between mb-2">
          <Label>Entry fields <span className="font-normal text-muted-foreground">(each entry in the group has these)</span></Label>
          <Button type="button" variant="outline" size="sm" className="rounded-lg" onClick={() => appendSub({ name: "", title: "", type: "string", required: false, list: [] })}>
            <Plus className="h-4 w-4" />Add sub-field
          </Button>
        </div>
        {subfields.map((sf, si) => (
          <SubFieldRow key={sf.id} form={form} fieldIndex={index} subIndex={si} remove={removeSub} />
        ))}
      </div>
    )}

    {/* Auto-fill */}
    {!isCalculated && (
      <div className="rc-12 mt-3 border-t pt-3">
        <FormField control={form.control} name={`fields.${index}.extraction.enabled`} render={({ field }) => (
          <FormItem className="flex items-center gap-2">
            <FormControl>
              <Checkbox checked={!!field.value} onCheckedChange={field.onChange} />
            </FormControl>
            <FormLabel className="mt-0! flex items-center gap-1.5 font-semibold text-violet-600">
              <Zap className="h-3.5 w-3.5" />Auto-fill this field
            </FormLabel>
          </FormItem>
        )} />

        {extractionEnabled && (
          <div className="mt-2 space-y-3 rounded-lg bg-violet-50/60 p-3">
            {/* Method toggle (keyword doesn't apply to repeating groups) */}
            {type !== "group" && <FormField control={form.control} name={`fields.${index}.extraction.method`} render={({ field }) => (
              <FormItem className="flex items-center gap-2">
                <FormLabel className="mt-0! text-xs text-muted-foreground">Method</FormLabel>
                <div className="flex gap-1">
                  {(["ai", "keyword"] as const).map((m) => (
                    <Button
                      key={m}
                      type="button"
                      size="sm"
                      variant={(field.value || "ai") === m ? "default" : "outline"}
                      className="h-7 text-xs capitalize"
                      onClick={() => field.onChange(m)}
                    >
                      {m === "ai" ? "AI" : "Keyword"}
                    </Button>
                  ))}
                </div>
              </FormItem>
            )} />}

            {type === "group" || method !== "keyword" ? (
              <>
                <FormField control={form.control} name={`fields.${index}.extraction.instruction`} render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Textarea
                        placeholder="Tell the agent how to fill this field. e.g. &quot;Classify the user's primary need into one of the options.&quot;"
                        rows={2}
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name={`fields.${index}.extraction.minConfidence`} render={({ field }) => (
                  <FormItem className="flex items-center gap-2">
                    <FormLabel className="mt-0! text-xs text-muted-foreground">Min confidence</FormLabel>
                    <FormControl>
                      <Input
                        type="number" step="0.05" min="0" max="1"
                        className="h-8 w-20"
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value === "" ? undefined : Number(e.target.value))}
                      />
                    </FormControl>
                  </FormItem>
                )} />
              </>
            ) : (
              <>
                <FormItem>
                  <FormLabel className="text-xs text-muted-foreground">Keywords (comma-separated)</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="urgent, emergency, asap"
                      value={keywords.join(", ")}
                      onChange={(e) => form.setValue(
                        `fields.${index}.extraction.keywords`,
                        e.target.value.split(",").map((s) => s.trim()).filter(Boolean)
                      )}
                    />
                  </FormControl>
                </FormItem>
                <div className="flex flex-wrap items-center gap-6">
                  <FormField control={form.control} name={`fields.${index}.extraction.keywordMatch`} render={({ field }) => (
                    <FormItem className="flex items-center gap-2">
                      <FormLabel className="mt-0! text-xs text-muted-foreground">Match</FormLabel>
                      <Select value={field.value || "any"} onValueChange={field.onChange}>
                        <FormControl><SelectTrigger className="h-8 w-40"><SelectValue /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="any">Any keyword</SelectItem>
                          <SelectItem value="all">All keywords</SelectItem>
                          <SelectItem value="phrase">Phrase</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )} />
                  {type !== "boolean" && (
                    <FormField control={form.control} name={`fields.${index}.extraction.keywordValue`} render={({ field }) => (
                      <FormItem className="flex items-center gap-2">
                        <FormLabel className="mt-0! text-xs text-muted-foreground">Store value</FormLabel>
                        <FormControl>
                          <Input placeholder="value to store on match" className="h-8 w-48" value={field.value ?? ""} onChange={field.onChange} />
                        </FormControl>
                      </FormItem>
                    )} />
                  )}
                </div>
              </>
            )}

            <FormField control={form.control} name={`fields.${index}.extraction.lockAfterHuman`} render={({ field }) => (
              <FormItem className="flex items-center gap-2">
                <FormControl>
                  <Checkbox checked={!!field.value} onCheckedChange={field.onChange} />
                </FormControl>
                <FormLabel className="mt-0! text-xs text-muted-foreground">Never overwrite a human edit</FormLabel>
              </FormItem>
            )} />
          </div>
        )}
      </div>
    )}

    {/* Calculated value */}
    <div className="rc-12 mt-3 border-t pt-3">
      <FormField control={form.control} name={`fields.${index}.calculated.formula`} render={({ field }) => (
        <FormItem>
          <FormLabel className="flex items-center gap-1.5 font-semibold text-slate-600">
            <Sigma className="h-3.5 w-3.5" />Calculated value <span className="font-normal text-muted-foreground">(optional)</span>
          </FormLabel>
          <FormControl>
            <Input
              placeholder={'Derive from other fields, e.g. "{quantity} * {unit_price}"'}
              value={field.value ?? ""}
              onChange={field.onChange}
            />
          </FormControl>
          <p className="text-[11px] text-muted-foreground mt-1">Reference other fields as {"{field_name}"}. Supports + − × ÷ and text join. Calculated fields are read-only.</p>
        </FormItem>
      )} />
    </div>

    {/* Conditional visibility */}
    <div className="rc-12 mt-3 border-t pt-3">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-1.5 font-semibold text-slate-600">
          <GitBranch className="h-3.5 w-3.5" />Show only when… <span className="font-normal text-muted-foreground">(optional)</span>
        </Label>
        <Button type="button" variant="outline" size="sm" className="rounded-lg" onClick={() => appendCond({ field: "", operator: "equals", value: "" })}>
          <Plus className="h-4 w-4" />Add condition
        </Button>
      </div>
      {conditions.map((cond, ci) => (
        <div key={cond.id} className="flex items-center gap-2 mt-2">
          <FormField control={form.control} name={`fields.${index}.visibleWhen.${ci}.field`} render={({ field }) => (
            <FormItem className="grow"><FormControl><Input placeholder="other field name" {...field} /></FormControl></FormItem>
          )} />
          <FormField control={form.control} name={`fields.${index}.visibleWhen.${ci}.operator`} render={({ field }) => (
            <FormItem>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl><SelectTrigger className="w-36"><SelectValue /></SelectTrigger></FormControl>
                <SelectContent>
                  {CONDITION_OPERATORS.map((op) => (
                    <SelectItem key={op} value={op}>{CONDITION_OPERATOR_LABELS[op]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormItem>
          )} />
          <FormField control={form.control} name={`fields.${index}.visibleWhen.${ci}.value`} render={({ field }) => (
            <FormItem className="grow"><FormControl><Input placeholder="value" value={(field.value as any) ?? ""} onChange={field.onChange} /></FormControl></FormItem>
          )} />
          <Button type="button" variant="ghost" size="icon" onClick={() => removeCond(ci)}><X className="h-4 w-4" /></Button>
        </div>
      ))}
    </div>
  </div>

}

function SubFieldRow({ form, fieldIndex, subIndex, remove }: { form: ReturnType<typeof useForm<FormData>>, fieldIndex: number, subIndex: number, remove: (i: number) => void }) {
  const base = `fields.${fieldIndex}.subfields.${subIndex}` as const
  const subType = form.watch(`${base}.type`)
  const { fields: listItems, append: appendItem, remove: removeItem } = useFieldArray({ control: form.control, name: `${base}.list` })

  return <div className="border rounded-md p-3 mb-2 bg-muted/20 relative">
    <Button type="button" variant="ghost" size="icon" className="absolute top-1.5 right-1.5 h-6 w-6" onClick={() => remove(subIndex)}>
      <X className="h-3.5 w-3.5 text-destructive" />
    </Button>
    <div className="flex flex-wrap gap-2 items-end pr-8">
      <FormField control={form.control} name={`${base}.name`} render={({ field }) => (
        <FormItem className="grow min-w-[120px]"><FormLabel className="text-xs">Name</FormLabel><FormControl><Input className="h-8" {...field} /></FormControl><FormMessage /></FormItem>
      )} />
      <FormField control={form.control} name={`${base}.title`} render={({ field }) => (
        <FormItem className="grow min-w-[120px]"><FormLabel className="text-xs">Title</FormLabel><FormControl><Input className="h-8" {...field} /></FormControl><FormMessage /></FormItem>
      )} />
      <FormField control={form.control} name={`${base}.type`} render={({ field }) => (
        <FormItem>
          <FormLabel className="text-xs">Type</FormLabel>
          <Select value={field.value} onValueChange={field.onChange} defaultValue={field.value}>
            <FormControl><SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger></FormControl>
            <SelectContent>
              <SelectItem value="string">Text</SelectItem>
              <SelectItem value="number">Number</SelectItem>
              <SelectItem value="boolean">Boolean</SelectItem>
              <SelectItem value="date">Date</SelectItem>
              <SelectItem value="list">List (single)</SelectItem>
              <SelectItem value="multiselect">Multi-select</SelectItem>
            </SelectContent>
          </Select>
        </FormItem>
      )} />
      <FormField control={form.control} name={`${base}.required`} render={({ field }) => (
        <FormItem className="flex items-center gap-1.5 pb-1.5">
          <FormControl><Checkbox checked={field.value} onCheckedChange={field.onChange} /></FormControl>
          <FormLabel className="mt-0! text-xs">Required</FormLabel>
        </FormItem>
      )} />
    </div>

    {(subType === "list" || subType === "multiselect") && (
      <div className="mt-2">
        <div className="flex items-center justify-between mb-1.5">
          <Label className="text-xs">Options</Label>
          <Button type="button" variant="outline" size="sm" className="h-7 rounded-lg" onClick={() => appendItem({ label: "", value: "" })}>
            <Plus className="h-3.5 w-3.5" />Add
          </Button>
        </div>
        {listItems.map((item, itemIndex) => (
          <div key={item.id} className="flex items-center gap-2 mb-1.5">
            <FormField control={form.control} name={`${base}.list.${itemIndex}.label`} render={({ field }) => (
              <FormItem className="grow"><FormControl><Input className="h-8" placeholder="Label" {...field} /></FormControl></FormItem>
            )} />
            <FormField control={form.control} name={`${base}.list.${itemIndex}.value`} render={({ field }) => (
              <FormItem className="grow"><FormControl><Input className="h-8" placeholder="Value" {...field} /></FormControl></FormItem>
            )} />
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeItem(itemIndex)}><X className="h-3.5 w-3.5" /></Button>
          </div>
        ))}
      </div>
    )}
  </div>
}
